import React, { useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { debounce } from 'lodash';

import { convertToQueryParams } from '../../../../utils/Helpers';
import { deleteWidget, fetchWidgetsList } from '../services/Dashboards.service';
import TableColumnsLoader from '../../../shared/Loader/tableColumnsLoader';

const AddWidgetsToDashboard = ({
  setDialogOpen,
  dialogOpen,
  setWidgetQuery,
  widgetQuery,
  dashboardId,
}) => {
  const [loadingWidgetsList, setLoadingWidgetsList] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState({
    limit: 10,
    offset: 0,
    search_query: '',
  });
  const [widgetsListData, setWidgetsListData] = React.useState();
  const [totalWidgets, setTotalWidgets] = React.useState(0);
  const [selectedWidgets, setSelectedWidgets] = React.useState([]);
  const [cubeQuery, setCubeQuery] = React.useState([]);

  const getWidgetsList = async queryParams => {
    setLoadingWidgetsList(true);
    const payload = Object.fromEntries(
      Object.entries(queryParams).filter(
        ([key, value]) => key !== 'search_query' || value?.trim()
      )
    );

    const queryString = convertToQueryParams(payload);
    const data = await fetchWidgetsList(queryString);

    if (!data?.error) {
      setWidgetsListData(data?.widgetsList);
      setTotalWidgets(data?.count);
    }

    setLoadingWidgetsList(false);
  };

  React.useEffect(() => {
    if (dialogOpen) {
      getWidgetsList(searchQuery);
    }
  }, [searchQuery.limit, searchQuery.offset, dialogOpen]);
  React.useEffect(() => {
    if (dialogOpen) {
      setSelectedWidgets(widgetQuery);
    }
  }, [dialogOpen]);

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        getWidgetsList({ ...searchQuery, search_query: value });
      }, 1000),
    []
  );
  const renderHeader = () => {
    return (
      <div className="flex justify-content-end">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={searchQuery?.search_query}
            onChange={event => {
              setSearchQuery(prev => ({
                ...prev,
                search_query: event.target.value,
              }));
              debouncedSearch(event.target.value);
            }}
            placeholder="Search"
          />
        </IconField>
      </div>
    );
  };
  const handleSelectWidgets = value => {
    const removedWidgetId = widgetQuery?.find(
      firstItem =>
        !value.some(
          secondItem =>
            secondItem.widget_template_id === firstItem.widget_template_id
        )
    )?.widget_id;
    if (removedWidgetId) {
      deleteWidget(
        removedWidgetId,
        {},
        dashboardId,
        () => {},
        () => {},
        () => {},
        setWidgetQuery
      );
    }

    const formattedWidgets = value?.map(widgetData => {
      const templateId =
        widgetData?.widget_template_id ??
        widgetData?.widget_template?.widget_template_id;

      const displayType =
        widgetData?.widget_display_type?.[0] ??
        widgetData?.widget_template?.widget_display_type?.[0] ??
        null;

      const name =
        widgetData?.widget_name ??
        widgetData?.widget_template?.widget_name ??
        '';
      const x_axis_position =
        widgetData?.x_axis_position ??
        widgetData?.widget_template?.x_axis_position ??
        null;
      const y_axis_position =
        widgetData?.y_axis_position ??
        widgetData?.widget_template?.y_axis_position ??
        null;
      const width =
        widgetData?.width ?? widgetData?.widget_template?.width ?? null;
      const height =
        widgetData?.height ?? widgetData?.widget_template?.height ?? null;

      return {
        index: templateId,
        widget_template_id: templateId,
        chart_type: displayType,
        width,
        x_axis_position,
        y_axis_position,
        height,
        name,
        dashboard_id: dashboardId,
        widget_template: {
          ...(widgetData?.widget_template ?? widgetData),
        },
      };
    });

    setSelectedWidgets(value);
    setCubeQuery(formattedWidgets);
  };

  const saveWidgets = () => {
    setSearchQuery({ limit: 10, offset: 0 });
    setWidgetQuery(cubeQuery);
    setDialogOpen(false);
  };

  const columns = [
    {
      field: 'multiple',
      header: '',
    },
    {
      field: 'widget_name',
      header: 'Name',
      headerStyle: { minWidth: '150px' },
    },
    {
      field: 'widget_description',
      header: 'Description',
      headerStyle: { minWidth: '150px' },
    },
  ];
  const tableLoader = useMemo(() => {
    return TableColumnsLoader(columns, {
      rows: 5,
      isValue: false,
    });
  }, [columns]);
  return (
    <Dialog
      header="Widgets List"
      visible={dialogOpen}
      style={{ width: '65vw' }}
      onHide={() => setDialogOpen(false)}
      footer={
        <div>
          <Button
            label="Cancel"
            onClick={() => setDialogOpen(false)}
            type="button"
            size="small"
          />
          <Button
            label="Confirm"
            size="small"
            type="button"
            onClick={saveWidgets}
          />
        </div>
      }
    >
      <DataTable
        header={renderHeader()}
        value={loadingWidgetsList ? tableLoader : widgetsListData}
        lazy
        paginator
        rows={searchQuery?.limit}
        totalRecords={totalWidgets}
        first={searchQuery?.offset}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 150, 200]}
        onPage={event => {
          setSearchQuery(prev => ({
            ...prev,
            offset: event.first,
            limit: event.rows,
          }));
        }}
        selection={selectedWidgets}
        onSelectionChange={e => handleSelectWidgets(e.value)}
        dataKey="widget_template_id"
        className="w-full add-widget-table pf-data-table p-datatable-striped"
      >
        {columns.map(col => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            selectionMode={col.field === 'multiple' && 'multiple'}
            headerStyle={col.headerStyle}
            showSelectAll={false}
          />
        ))}
      </DataTable>
    </Dialog>
  );
};

export default AddWidgetsToDashboard;
